<script>
import axios from 'axios';
import sentry from '@/analytics/sentry';
import { CONTENT_RISK_STATUS, CONTENT_RISK_QUALITY } from './content-risk.constants';

export default {
    data() {
        return {
            contentRisks: null,
            subjectRisks: null,
            previewRisks: null,
            contentRiskStatus: CONTENT_RISK_STATUS.NONE,
            subjectRiskStatus: CONTENT_RISK_STATUS.NONE,
            previewRiskStatus: CONTENT_RISK_STATUS.NONE,
            contentRiskRequestCounter: 0,
            previewRiskRequestCounter: 0,
        };
    },

    computed: {
        contentRiskSendable() {
            return this.contentRisks?.quality !== CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE;
        },

        contentRiskQuality() {
            return this.contentRisks?.quality ?? CONTENT_RISK_QUALITY.OK;
        },

        contentRiskMessages() {
            return this.contentRisks?.details ?? [];
        },

        loadingContentRisk() {
            return this.contentRiskStatus === CONTENT_RISK_STATUS.LOADING;
        },

        subjectRiskSendable() {
            return this.subjectRisks?.quality !== CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE;
        },

        subjectRiskQuality() {
            return this.subjectRisks?.quality ?? CONTENT_RISK_QUALITY.OK;
        },

        subjectRiskMessages() {
            return this.subjectRisks?.details ?? [];
        },

        loadingSubjectRisk() {
            return this.subjectRiskStatus === CONTENT_RISK_STATUS.LOADING;
        },

        previewRiskSendable() {
            return this.previewRisks?.quality !== CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE;
        },

        previewRiskQuality() {
            return this.previewRisks?.quality ?? CONTENT_RISK_QUALITY.OK;
        },

        previewRiskMessages() {
            return this.previewRisks?.details ?? [];
        },

        loadingPreviewRisk() {
            return this.previewRiskStatus === CONTENT_RISK_STATUS.LOADING;
        },
    },

    methods: {
        async queryContentRisk({ content, subject }) {
            const contentRiskListRequest = {
                subject,
                content,
            };

            try {
                const { data } = await axios.post(
                    '/app/unlayerEmailFunnel/contentRisk/list',
                    contentRiskListRequest,
                );

                const { quality, details } = data;

                return {
                    quality,
                    details,
                };
            } catch (error) {
                sentry.captureException(error);

                throw error;
            }
        },

        async fetchContentRisks(content) {
            if (!content) {
                return;
            }

            this.contentRiskRequestCounter++;
            const { contentRiskRequestCounter: localCount } = this;

            try {
                this.contentRiskStatus = CONTENT_RISK_STATUS.LOADING;

                const contentRisks = await this.queryContentRisk({ content });

                if (this.contentRiskRequestCounter !== localCount) {
                    return;
                }

                this.contentRisks = contentRisks;
                this.contentRiskStatus = CONTENT_RISK_STATUS.NONE;
            } catch (error) {
                this.contentRiskStatus = CONTENT_RISK_STATUS.FAILED;
            }
        },

        async fetchSubjectRisks(subject) {
            if (!subject) {
                return;
            }

            try {
                this.subjectRiskStatus = CONTENT_RISK_STATUS.LOADING;
                this.subjectRisks = await this.queryContentRisk({ subject });
                this.subjectRiskStatus = CONTENT_RISK_STATUS.NONE;
            } catch (error) {
                this.subjectRiskStatus = CONTENT_RISK_STATUS.FAILED;
            }
        },

        async fetchPreviewRisks(preview) {
            this.previewRiskRequestCounter += 1;
            const localCounter = this.previewRiskRequestCounter;

            if (!preview) {
                this.previewRiskStatus = CONTENT_RISK_STATUS.NONE;
                this.previewRisks = {
                    quality: CONTENT_RISK_QUALITY.OK,
                };

                return;
            }

            try {
                this.previewRiskStatus = CONTENT_RISK_STATUS.LOADING;

                const previewRisks = await this.queryContentRisk({ subject: preview });

                if (localCounter !== this.previewRiskRequestCounter) {
                    return;
                }

                this.previewRisks = previewRisks;
                this.previewRiskStatus = CONTENT_RISK_STATUS.NONE;
            } catch (e) {
                this.previewRiskStatus = CONTENT_RISK_STATUS.FAILED;
            }
        },
    },

    render() {
        const {
            subjectRiskSendable,
            loadingSubjectRisk,
            subjectRiskQuality,
            subjectRiskMessages,
            fetchSubjectRisks,

            contentRiskSendable,
            loadingContentRisk,
            contentRiskQuality,
            contentRiskMessages,
            fetchContentRisks,
            contentRiskStatus,

            previewRiskSendable,
            previewRiskQuality,
            previewRiskMessages,
            loadingPreviewRisk,
            fetchPreviewRisks,
        } = this;

        return this.$scopedSlots.default({
            subjectRiskSendable,
            loadingSubjectRisk,
            subjectRiskQuality,
            subjectRiskMessages,
            fetchSubjectRisks,

            contentRiskSendable,
            loadingContentRisk,
            contentRiskQuality,
            contentRiskMessages,
            fetchContentRisks,
            contentRiskStatus,

            previewRiskSendable,
            previewRiskQuality,
            previewRiskMessages,
            loadingPreviewRisk,
            fetchPreviewRisks,
        });
    },
};
</script>
